/* eslint-disable import/prefer-default-export */
import * as React from "react";
import {
  makeStyles,
  StyleRules,
} from "@material-ui/core";
import { Auth } from "aws-amplify";
import {
  getSessionData,
  PentairBlue,
  PentairTheme,
} from "@pentair/react";
import {
  IFavorite,
  IFeature,
  IMicroAppFeature,
} from "./types";
import {
  IconButton,
  FavoriteButton,
  LinkIconButton,
  ProfileIconButton,
} from "../../components/mui-extended/mui-icon-button";
import {
  BROWSER_STORE_FAVORITES,
  FAQ_PAGE_PATH,
  LANDING_PAGE_OBJECT_KEY,
  MICRO_APP_DASHBOARDS,
  MICRO_APP_LOGIC_LAYER,
  MICRO_APP_USER_MANAGEMENT,
  PERSONAL_PROFILE,
  buildLogicLayerNavPath,
} from "../../config/constants";
import {
  onNavigate,
  route,
} from "../../config/routes";
import { ToolBarItem } from "./partials/toolbar-dock-item";
import { Tooltip } from "./partials/tooltip";
import { updateMicroAppDetails } from "../../utils/commonUtils";
import { NavigationToolbarItem } from "./partials/navigation-toolbar-item";
import { INavigationConfig, INavNode } from "../administration/partials/navigation-menu-section";
import { useFeatureAccessContext } from "../../context/FeatureAccessContext";

const PROFILE_PAGE_PATH = "personalprofile/myprofile";

/**
 * Component props
 */
interface IOwnProps {
  // List of micro-apps
  microApps: IMicroAppFeature[];
  // Navigation configs from API
  navigationConfigs: INavigationConfig[];
  // On click of open favorite side bar
  openFavoriteSideDrawer: (openDrawer: boolean) => void;
  // On click of open notification side bar
  openNotificationSideDrawer: (openDrawer: boolean) => void;
}

/**
 * Component UX styles
 */
const useStyles = makeStyles((theme: PentairTheme): StyleRules => ({
  root: {
    position: "fixed",
    top: 0,
    left: 0,
    width: 48,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: PentairBlue[500],
    "& > nav": {
      display: "flex",
      flexDirection: "column",
    },
    [theme.breakpoints.between("xs", "sm")]: {
      display: "none",
    },
  },
}));

// Drop features whose normalized name already appeared earlier in the list so
// the sidebar never shows the same icon twice (e.g. duplicate "Global" entries
// produced when permissions resolve a feature that metadata also surfaces).
const dedupeByName = (features: IFeature[] = []): IFeature[] => {
  const seen = new Set<string>();
  const out: IFeature[] = [];
  features.forEach((f) => {
    const key = (f?.name || "").toString().toLowerCase().replace(/[\s_-]+/g, "");
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(f);
  });
  return out;
};

/**
 * Left Navigation tool bar.
 */
export const ToolBar: React.FC<IOwnProps> = (props) => {
  // Style object
  const classes = useStyles();

  // Access state constants
  const {
    microApps,
    navigationConfigs,
    openFavoriteSideDrawer,
    openNotificationSideDrawer,
  } = props;

  /// Hold the username captured from user session
  const [userName, setUserName] = React.useState("");

  /// Hold User Management-Micro app-domain
  const [userManagementDomain, setUserManagementDomain] = React.useState("");

  /// Hold Operations-Micro app-domain
  const [operationsApp, setOperationsApp] = React.useState("");

  /// Hold User Management-Micro app-profile page route
  const [profilePermission, setProfilePermission] = React.useState(true);

  /// Hold sorted list of all Micro apps
  const [localMicroApps, setLocalMicroApps] = React.useState<IMicroAppFeature[]>([]);
  React.useEffect(() => {
    Auth.currentSession()
      .then((user: any) => {
        setUserName(
          user.idToken.payload.family_name[0].toUpperCase()
          + user.idToken.payload.given_name[0].toUpperCase(),
        );
      })
      .catch((error: any) => { });

    /// Set sorted list of all Micro apps
    const availableMicroappList = updateMicroAppDetails(microApps);
    setLocalMicroApps(availableMicroappList?.sort((a: IMicroAppFeature, b: IMicroAppFeature) => Number(a.order) - Number(b.order)));

    /// Set Operations-Micro app-domain
    setOperationsApp(availableMicroappList.find((microApp: IMicroAppFeature) => microApp.microapp === MICRO_APP_DASHBOARDS)?.domain || "");
    const userManagementApps = availableMicroappList.find((microApp: IMicroAppFeature) => microApp.microapp === MICRO_APP_USER_MANAGEMENT);
    setUserManagementDomain(userManagementApps?.domain || "");

    /// Set User Management-Micro app-profile page route
    const userProfile = userManagementApps?.features?.find((features: IFeature) => features.name === PERSONAL_PROFILE);
    setProfilePermission(!!(userProfile && userProfile?.permissions.length > 0));
  }, [microApps]);

  // Replacement and standalone configs
  const replacementConfigs = navigationConfigs?.filter((config) => config.path && config.path !== "");
  const replacedMicroApps = new Set(replacementConfigs?.map((config) => config.path));
  const standaloneConfigs = navigationConfigs?.filter((config) => !config.path || config.path === "");

  /**
   * Checks if current page is favorite page
   * @returns true or false
   */
  const isFavoritePage = () => {
    try {
      const favorites = getSessionData(BROWSER_STORE_FAVORITES, []) as IFavorite[];
      if (favorites && favorites.length) {
        const favoritesArr: IFavorite[] = favorites;
        const landingPage = favoritesArr?.find((fav: IFavorite) => fav.key === LANDING_PAGE_OBJECT_KEY);
        if (landingPage && landingPage.value) {
          const favoriteRoute = JSON.parse(landingPage.value);
          return favoriteRoute && favoriteRoute.pathname === window.location.pathname;
        }
      }
    } catch (err) {
      ///
    }
    return false;
  };

  const logicLayerApp = localMicroApps?.find((app) => app.microapp === MICRO_APP_LOGIC_LAYER);
  const featureAccess = useFeatureAccessContext();

  return (
    <aside className={classes.root}>
      <div>
        <nav style={{ display: "none" }}>
          <IconButton
            variant="primary"
            icon="bell"
            onClick={() => openNotificationSideDrawer(true)}
          />
        </nav>
        {localMicroApps.map((microApp: IMicroAppFeature, index: any) => {
          // Filter and ignore the Micro apps with empty `domain`
          if (!microApp.domain || microApp.domain === "") {
            return null;
          }
          if (microApp.microapp === MICRO_APP_LOGIC_LAYER && !featureAccess?.canAccess(microApp.microapp)) {
            return null;
          }
          if (replacedMicroApps.has(microApp.microapp) && logicLayerApp) {
            const navConfig = replacementConfigs.find((config) => config.path === microApp.microapp);
            if (navConfig) {
              return (
                <NavigationToolbarItem
                  key={`nav-toolbar-${navConfig.id}`}
                  navigationConfigs={[navConfig]}
                  microAppFeature={logicLayerApp}
                />
              );
            }
          }
          /**
           * Check whether 'displayToolbar' enabled for App level
           * If YES-> Then check list of features and check for 'isDefaultPath=true'
           *          if multiple are there then get the first one from list of feature
           *          And prepare the icons list in sorted way.
           * If NO -> Then check list of features and check for 'displayToolbar=true'
           *          And prepare the icons list in sorted way. */
          const features = microApp?.features?.sort((a: IFeature, b: IFeature) => Number(a.order) - Number(b.order));
          if ((microApp.displayInToolbar === null || microApp.displayInToolbar === true) && features?.length) {
            const defaultFeature = features?.find((fe: IFeature) => fe.isDefaultPath === "true") || features[0];
            // Include any additional features explicitly flagged for the toolbar
            const extraToolbarFeatures = features.filter(
              (fe: IFeature) => fe.displayInToolbar === true && fe.name !== defaultFeature?.name,
            );
            const featuresToRender = dedupeByName(
              defaultFeature ? [defaultFeature, ...extraToolbarFeatures] : extraToolbarFeatures,
            );
            if (featuresToRender.length) {
              return (
                <ToolBarItem
                  key={`child-app-key+${index}`}
                  features={featuresToRender}
                  microApp={microApp}
                />
              );
            }
            return null;
          } if (features?.length) {
            return (
              <ToolBarItem
                key={`child-feature-key+${index}`}
                features={dedupeByName(
                  features.filter((f: IFeature) => f.displayInToolbar !== null && f.displayInToolbar === true),
                )}
                microApp={microApp}
              />
            );
          } return null;
        })}
        {standaloneConfigs?.length > 0 && logicLayerApp && (
          <NavigationToolbarItem
            navigationConfigs={standaloneConfigs}
            microAppFeature={logicLayerApp}
          />
        )}
      </div>
      <div style={{ paddingBottom: 16 }}>
        <Tooltip title="Favorite" placement="right" arrow>
          <nav style={{ height: 40 }}>
            <FavoriteButton
              variant="primary"
              icon="star"
              style={{ height: "auto", padding: 0 }}
              active={isFavoritePage()}
              onClick={() => openFavoriteSideDrawer(true)}
              data-testid="fav-button"
            />
          </nav>
        </Tooltip>
        <Tooltip title="Home" placement="right" arrow>
          <nav style={{ height: 48 }}>
            <LinkIconButton variant="primary" icon="cog" to={route("app.shell.administration")} />
          </nav>
        </Tooltip>
        {profilePermission && (
          <Tooltip title={PERSONAL_PROFILE} placement="right" arrow>
            <nav style={{ height: 48 }}>
              <ProfileIconButton
                variant="primary"
                userName={userName}
                onClick={() => {
                  // Check if Personal Profile has a navigation config replacement
                  const profileConfig = navigationConfigs?.find((config) => config.path === PERSONAL_PROFILE);
                  if (profileConfig && logicLayerApp) {
                    // Find first visible page in the config
                    const firstPage = (profileConfig.children || [])
                      .filter((n: INavNode) => n.type === "folder" && n.isVisible)
                      .sort((a: INavNode, b: INavNode) => a.order - b.order)
                      .flatMap((folder: INavNode) =>
                        (folder.children || []).filter((c: INavNode) => c.type === "page" && c.isVisibleInMenu).sort((a: INavNode, b: INavNode) => a.order - b.order)
                      )[0];
                    if (firstPage) {
                      onNavigate(
                        logicLayerApp.microapp,
                        buildLogicLayerNavPath(profileConfig.id, firstPage.path),
                        logicLayerApp.domain,
                      );
                      return;
                    }
                  }
                  // Fallback: existing behavior
                  onNavigate(
                    MICRO_APP_USER_MANAGEMENT,
                    PROFILE_PAGE_PATH,
                    userManagementDomain,
                  );
                }}
              />
            </nav>
          </Tooltip>
        )}
        <Tooltip title="Support" placement="right" arrow>
          <nav>
            <IconButton
              data-testid="faq-button"
              variant="primary"
              icon={"headset"}
              key={"faq"}
              onClick={() => {
                onNavigate(
                  MICRO_APP_DASHBOARDS,
                  FAQ_PAGE_PATH,
                  operationsApp,
                )
              }
              }
            />
          </nav>
        </Tooltip>
      </div>
    </aside>
  );
};
